import type { QueryTarget } from "@/shared/domain/query";
import type { SortDirection } from "@/types";

import type { SavedFilters } from "./savedFilters";

/**
 * Identifier for the first version of the saved View definition schema.
 *
 * Every definition records the scheme it was written under, so a later
 * `view_v2` can be recognised and upgraded rather than misread as this shape.
 */
export const VIEW_SCHEME_V1 = "view_v1";

/**
 * How a View presents its results: which columns, in what order, sorted how,
 * how many rows. Presentation only — nothing here changes which records match.
 */
export interface ViewDisplay {
  /**
   * Columns to show, in display order. An empty list records no preference, and
   * restores as `null` so the caller applies its own defaults.
   */
  visible_columns: string[];
  /** Field to sort by, or `null` to leave the target's default sort in place. */
  sort_by: string | null;
  /** Direction to sort in. Only meaningful alongside `sort_by`. */
  sort_direction: SortDirection | null;
  /** Rows per page, or `null` to accept whatever the table would pick on its own. */
  page_size: number | null;
}

/**
 * The persisted contents of a View.
 *
 * Stores what the author expressed, not the query it compiles to — a stored
 * query would resolve "last 7 days" to fixed instants and then show the wrong
 * week forever.
 *
 * `roboql` and `filters` are mutually exclusive. Neither set is an unfiltered
 * View, saved for its columns and sort.
 */
export interface ViewDefinitionV1 {
  scheme: string;
  /** The RoboQL text the author typed. `null` for a View built from filter controls. */
  roboql: string | null;
  /** The filter controls the author built. `null` for a RoboQL View. */
  filters: SavedFilters | null;
  display: ViewDisplay;
}

/**
 * Who can see a View. Derived from OpenFGA rather than stored on the row, and
 * returned on reads so the picker can group Views without a call per row.
 */
export type ViewVisibility = "private" | "organization";

/**
 * A named, org-scoped, shareable search over one resource type.
 *
 * Mirrors `roboto.domain.views.ViewRecord`.
 */
export interface ViewRecord {
  view_id: string;
  name: string;
  org_id: string;
  /** The resource type this View searches. Fixed at creation. */
  target: QueryTarget;
  definition: ViewDefinitionV1;
  /** Mirrors `definition.scheme`, so rows can be selected by version without parsing JSON. */
  schema_version: number;
  created: string;
  /** The author. Alone may delete the View or change who can see it. */
  created_by: string;
  modified: string;
  modified_by: string;
  /**
   * Who can see this View. Responses fill this in; `undefined` means the
   * question was not resolved, which is not the same as private — defaulting it
   * that way would file a shared View under a personal heading.
   */
  visibility?: ViewVisibility;
}

export interface CreateViewRequest {
  name: string;
  target: QueryTarget;
  definition: ViewDefinitionV1;
  /** Omit to take the backend default, `"private"`. */
  visibility?: ViewVisibility;
}

/**
 * Only what is being changed. No `target`: filters are written against one
 * target's fields, so changing it would strand them.
 */
export interface UpdateViewRequest {
  name?: string;
  definition?: ViewDefinitionV1;
}

export function isViewRecord(value: unknown): value is ViewRecord {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<ViewRecord>;
  return (
    typeof candidate.view_id === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.org_id === "string" &&
    typeof candidate.target === "string" &&
    typeof candidate.definition === "object" &&
    candidate.definition !== null
  );
}
